import { useTranslation } from "react-i18next";
import { addDays, parseDateKey, splitDuration } from "../logic/dayTime";
import { calendarDaysSince, formatDaysAgo } from "../logic/guestHistory";

const localeOf = (language: string) => (language.startsWith("en") ? "en-GB" : "bg-BG");

const capitalize = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1);

/**
 * Преведените форми, които модулът показва навсякъде (поповър, табове,
 * списък): продължителност „1 ч 15 мин", „5 места", „2 гости", „Маса 4",
 * заглавието на деня.
 */
export const useReservationFormat = () => {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language ?? "bg");

  const duration = (totalMinutes: number) => {
    const { hours, minutes } = splitDuration(Math.max(0, totalMinutes));
    if (hours === 0) return t("reservations.duration.minutes", { minutes });
    if (minutes === 0) return t("reservations.duration.hours", { hours });
    return t("reservations.duration.hoursMinutes", { hours, minutes });
  };

  /**
   * Сгъстената форма за надписа на МАЛКА маса, където „4 ч 52 мин" не се
   * събира: „4:52 ч"; под час остава „35 мин".
   */
  const compactDuration = (totalMinutes: number) => {
    const { hours, minutes } = splitDuration(Math.max(0, totalMinutes));
    if (hours === 0) return t("reservations.duration.minutes", { minutes });
    return t("reservations.duration.hours", { hours: `${hours}:${String(minutes).padStart(2, "0")}` });
  };

  /**
   * Продължителност на резервация в часове с десетична запетая: „2 ч",
   * „1,5 ч"; некратна на половин час — „1 ч 15 мин".
   */
  const hours = (totalMinutes: number) =>
    totalMinutes % 30 === 0
      ? t("reservations.duration.hours", {
          hours: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(totalMinutes / 60),
        })
      : duration(totalMinutes);

  /** „петък, 25 септември 2026" (без „г." на Intl). */
  const longDay = (dateKey: string) => {
    const parts = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).formatToParts(parseDateKey(dateKey));
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((candidate) => candidate.type === type)?.value ?? "";
    return `${part("weekday")}, ${part("day")} ${part("month")} ${part("year")}`;
  };

  /** „25.09" */
  const shortDate = (dateKey: string) => {
    const [, month, day] = dateKey.split("-");
    return `${day}.${month}`;
  };

  /** „Пт" */
  const shortWeekday = (dateKey: string) =>
    capitalize(
      new Intl.DateTimeFormat(locale, { weekday: "short" })
        .format(parseDateKey(dateKey))
        .replace(".", ""),
    );

  /** „1, 2 и 3" — изброяване на маси в изречение. */
  const list = (items: string[]) => new Intl.ListFormat(locale, { type: "conjunction" }).format(items);

  /** „днес" / „вчера" / „преди 19 дни" / „преди 2 месеца" — колко отдавна е денят на момента. */
  const daysAgo = (iso: string, nowMs: number) => formatDaysAgo(calendarDaysSince(iso, nowMs), locale);

  /** „Днес" / „Утре" / „Пт, 03.10" — денят спрямо днес. */
  const relativeDay = (dateKey: string, todayKey: string) => {
    if (dateKey === todayKey) return t("reservations.form.today");
    if (dateKey === addDays(todayKey, 1)) return t("reservations.form.tomorrow");
    return `${shortWeekday(dateKey)}, ${shortDate(dateKey)}`;
  };

  return {
    duration,
    compactDuration,
    hours,
    seats: (count: number) => t("reservations.counts.seats", { count }),
    guests: (count: number) => t("reservations.counts.guests", { count }),
    tableName: (name: string) => t("reservations.table.name", { name }),
    list,
    longDay,
    shortDate,
    shortWeekday,
    relativeDay,
    daysAgo,
  };
};

export type ReservationFormat = ReturnType<typeof useReservationFormat>;
